import { useMetaTags } from "../components/MetaTags";

export function PrivacyPolicy() {
  useMetaTags({
    title: "Privacy Policy | OmniCanvas",
    description: "OmniCanvas privacy policy — how we collect, use, and protect your data.",
    url: "https://omnicanvasnotes.com/privacy",
    robots: "noindex, follow",
  });

  return (
    <div className="pt-28 pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <p className="text-sm text-slate-400 mb-2">Last updated: July 28, 2026</p>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 mb-8">
          Privacy Policy
        </h1>

        <div className="prose prose-slate max-w-none space-y-8 text-slate-600 leading-relaxed">
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Data We Collect</h2>
            <p>
              We collect the minimum data necessary to provide OmniCanvas. This includes your
              email address when you create an account or subscribe to our newsletter, and the
              notes and canvas data you choose to sync with our cloud service. If you use
              OmniCanvas offline only, no personal data is transmitted to our servers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Cookies</h2>
            <p>
              We use first-party cookies and browser storage for authentication, preferences,
              and product analytics. A pseudonymous visitor identifier and first-touch campaign
              details can be shared between omnicanvasnotes.com and app.omnicanvasnotes.com so
              we can understand which pages lead to useful product activity and signups. These
              identifiers can remain in your browser for up to 400 days. We do not use them for
              advertising or tracking across other companies&apos; sites.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Product Analytics</h2>
            <p>
              We record page paths, campaign and referring-site details, button and template
              interactions, and aggregate milestones such as creating or returning to a note.
              Events include pseudonymous visitor and session identifiers; after sign-in they
              can also include your internal account ID so we can measure retention. Product
              analytics do not include note or canvas contents, form input, passwords, auth
              tokens, or your email address.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Third-Party Services</h2>
            <p>We use the following third-party services:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Cloudflare</strong> &mdash; hosting, CDN, and analytics</li>
              <li><strong>Pushmail</strong> &mdash; transactional and newsletter emails</li>
              <li>
                <strong>
                  <a
                    href="https://glitchreplay.com"
                    className="text-indigo-600 hover:text-indigo-700 underline"
                  >
                    GlitchReplay
                  </a>
                </strong>{" "}
                &mdash; product analytics and browser-side error monitoring (operated by InventiveHQ)
              </li>
            </ul>
            <p className="mt-2">
              These services process data on our behalf and are bound by their own privacy
              policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Error Monitoring</h2>
            <p>
              We use{" "}
              <a
                href="https://glitchreplay.com"
                className="text-indigo-600 hover:text-indigo-700 underline"
              >
                GlitchReplay
              </a>
              , an error-tracking service operated by InventiveHQ, to capture browser-side
              errors so we can identify and fix bugs.
            </p>
            <p className="mt-2">
              When an uncaught JavaScript error, unhandled promise rejection, or failed
              resource load occurs in your browser, we send GlitchReplay:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>The error message and stack trace</li>
              <li>The URL of the page where the error happened</li>
              <li>Browser, operating system, and rough geographic region (country)</li>
              <li>A timestamp</li>
            </ul>
            <p className="mt-2">
              We do not send: form input, cookies, localStorage, auth tokens, or any directly
              identifying personal information.
            </p>
            <p className="mt-2">
              Data is stored on Cloudflare infrastructure in the United States and retained
              for up to 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Data Retention</h2>
            <p>
              Your account data and synced notes are retained for as long as your account is
              active. If you delete your account, all associated data will be permanently
              removed from our servers within 30 days. Newsletter subscriptions can be
              cancelled at any time.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Request a copy of all data we hold about you</li>
              <li>Export your notes and canvas data at any time</li>
              <li>Request deletion of your account and all associated data</li>
              <li>Unsubscribe from marketing communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Contact</h2>
            <p>
              If you have questions about this privacy policy or your data, contact us at{" "}
              <a
                href="mailto:hello@omnicanvasnotes.com"
                className="text-indigo-600 hover:text-indigo-700 underline"
              >
                hello@omnicanvasnotes.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
